import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

type StatItem = {
  id: string; // Database se match karne ke liye ID add kiya hai
  count: string;
  label: string;
  hindiLabel: string;
  color: string;
  icon: string;
  text: string;
  description: string;
};

type CauseItem = {
  title: string;
  hindiTitle: string;
  tag: string;
  img: string;
  link: string;
  alt: string;
  keywords: string;
};

const defaultStats: StatItem[] = [
  {
    id: "health_nutrition",
    count: "1,21,480+",
    label: "Health & Nutrition",
    hindiLabel: "स्वास्थ्य और पोषण",
    color: "bg-pink-50",
    icon: "🌸",
    text: "text-pink-600",
    description:
      "Healthcare support, health awareness, nutrition support, medical camps and swasthya seva for underprivileged communities.",
  },
  {
    id: "education_projects",
    count: "90,252+",
    label: "Education Projects",
    hindiLabel: "शिक्षा अभियान",
    color: "bg-emerald-50",
    icon: "📚",
    text: "text-emerald-600",
    description:
      "Child education support, school kit donation, learning resources and bachchon ki padhai for poor children.",
  },
  {
    id: "economic_wellbeing",
    count: "61,007+",
    label: "Economic Well-being",
    hindiLabel: "आर्थिक सशक्तिकरण",
    color: "bg-orange-50",
    icon: "🏆",
    text: "text-orange-600",
    description:
      "Livelihood support, skill development, kaushal vikas, self-reliance and employment awareness initiatives.",
  },
  {
    id: "child_protection",
    count: "30,250+",
    label: "Child Protection",
    hindiLabel: "बाल सुरक्षा",
    color: "bg-amber-50",
    icon: "🛡️",
    text: "text-amber-600",
    description:
      "Child protection, child welfare, safe childhood, education support and poor children help programs.",
  },
  {
    id: "participation",
    count: "26,530+",
    label: "Participation",
    hindiLabel: "जन भागीदारी",
    color: "bg-teal-50",
    icon: "🤝",
    text: "text-teal-600",
    description:
      "Volunteer participation, CSR support, NGO partnership, charity drives and community involvement.",
  },
  {
    id: "humanitarian_work",
    count: "21,858+",
    label: "Humanitarian Work",
    hindiLabel: "मानवीय सेवा",
    color: "bg-purple-50",
    icon: "🚀",
    text: "text-purple-600",
    description:
      "Humanitarian aid, hunger support, food donation, ration distribution, emergency relief and samaj seva.",
  },
];

const causes: CauseItem[] = [
  {
    title: "TAYYARI KAL KI",
    hindiTitle: "भविष्य की तैयारी",
    tag: "LIVELIHOOD",
    img: "https://img.freepik.com/free-photo/young-indian-girl-portrait_23-2151996283.jpg?semt=ais_rp_progressive&w=740&q=80",
    link: "/csr",
    alt: "Livelihood support and skill development initiative by Sevaa Foundation NGO",
    keywords:
      "Empowering youth through livelihood programs, skill development, and employment assistance across India.",
  },
  {
    title: "HEALTH CANNOT WAIT",
    hindiTitle: "स्वास्थ्य सेवा",
    tag: "HEALTHCARE",
    img: "https://media.istockphoto.com/id/1252924066/photo/poor-indian-children-asking-for-help.jpg?s=612x612&w=0&k=20&c=iCfnyEJBALdrB8D_EZ0CI1lDgDHMZ8yCbwua6eNyEQ8=",
    link: "/medical-blood-donation",
    alt: "Healthcare support, medical camp and blood donation work by Sevaa Foundation",
    keywords:
      "Providing accessible healthcare via medical camps, blood donation drives, and rural swasthya seva.",
  },
  {
    title: "SHIKSHA NA RUKE",
    hindiTitle: "शिक्षा न रुके",
    tag: "EDUCATION",
    img: "https://images.bhaskarassets.com/web2images/521/2021/07/24/b1_1627129592.jpg",
    link: "/child-education",
    alt: "Child education support for poor children by Sevaa Foundation NGO",
    keywords:
      "Ensuring quality education for underprivileged children by providing school kits and academic support.",
  },
  {
    title: "SHE CAN FLY",
    hindiTitle: "महिला सशक्तिकरण",
    tag: "GIRL CHILD",
    img: "https://st.depositphotos.com/1099671/4071/i/950/depositphotos_40719849-stock-photo-poor-children.jpg",
    link: "/women-empowerment",
    alt: "Girl child support and women empowerment initiative by Sevaa Foundation NGO",
    keywords:
      "Championing women empowerment and girl child education to build a more equitable and self-reliant society.",
  },
];

const WhatWeDo = () => {
  const [dynamicStats, setDynamicStats] = useState<StatItem[]>(defaultStats);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // const res = await fetch(`http://localhost/seva-backend/api/stats/get_stats.php?t=${new Date().getTime()}`);
        const res = await fetch(`https://www.sevaafoundation.com/seva-backend/api/stats/get_stats.php?t=${new Date().getTime()}`);
        const data = await res.json();
        
        if (data.success && data.stats) {
          setDynamicStats(prevStats => 
            prevStats.map(stat => ({
              ...stat,
              count: data.stats[stat.id] || stat.count
            }))
          );
        }
      } catch (error) {
        console.error("Failed to load dynamic stats", error);
      }
    };
    fetchStats();
  }, []);

  return (
    <section
      id="what-we-do"
      aria-labelledby="what-we-do-heading"
      className="bg-white font-poppins pb-20 overflow-hidden"
    >
      <div className="relative py-16 bg-gradient-to-b from-slate-50 to-white">
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          aria-hidden="true"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="text-left"
          >
            <div className="inline-block px-4 py-1.5 rounded-full bg-orange-100 mb-6">
              <span className="text-orange-700 font-black text-[10px] uppercase tracking-[0.2em]">
                Our 2025 Impact Report
              </span>
            </div>

            <h2
              id="what-we-do-heading"
              className="text-4xl md:text-6xl font-black text-slate-900 leading-[0.95] tracking-tighter"
            >
              Making a <br />
              <span className="text-orange-600">Real Difference</span> <br />
              Every Single Day.
            </h2>

            <p className="text-slate-500 mt-8 text-lg leading-relaxed max-w-md font-medium">
              Sevaa Foundation works for sustainable development through child
              education, healthcare support, women empowerment, hunger relief,
              livelihood support and social welfare for underprivileged
              communities across India.
            </p>

            <p className="sr-only">
              Sevaa Foundation ek registered NGO hai jo Bharat me bachchon ki
              padhai, healthcare camp, blood donation, mahila sashaktikaran,
              bhojan sahayata, tree plantation, old age home support, charity
              donation, CSR partnership aur samaj seva ke liye kaam karta hai.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/about"
                className="bg-[#0f766e] text-white font-bold text-sm px-10 py-4 rounded-2xl shadow-xl transform transition-all duration-300 ease-in-out hover:bg-slate-900 hover:-translate-y-1 hover:scale-105 hover:shadow-2xl text-center"
                aria-label="Read Sevaa Foundation mission and NGO work"
              >
                Our Mission
              </Link>

              <Link
                to="/csr"
                className="bg-white border-2 border-slate-200 text-slate-900 font-bold text-sm px-10 py-4 rounded-2xl transform transition-all duration-300 ease-in-out hover:bg-slate-50 hover:-translate-y-1 hover:scale-105 hover:shadow-lg text-center"
                aria-label="Partner with Sevaa Foundation for CSR and social welfare"
              >
                Partner With Us
              </Link>
            </div>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {dynamicStats.map((stat, index) => (
              <motion.article
                key={stat.id}
                whileHover={{ y: -5 }}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className={`${stat.color} p-6 rounded-[1.5rem] flex flex-col justify-between h-52 border border-black/5 shadow-sm transition-all`}
                aria-label={`${stat.count} ${stat.label} by Sevaa Foundation`}
              >
                <div
                  className="bg-white w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shadow-sm"
                  aria-hidden="true"
                >
                  {stat.icon}
                </div>

                <div>
                  <h3 className={`text-2xl font-black ${stat.text} leading-none`}>
                    {stat.count}
                  </h3>

                  <p className={`text-[10px] font-bold uppercase mt-2 opacity-80 ${stat.text} leading-tight`}>
                    {stat.label}
                  </p>

                  <p className={`text-[10px] font-bold mt-1 ${stat.text}`}>
                    {stat.hindiLabel}
                  </p>

                  <p className="sr-only">{stat.description}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </div>

      <div className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
            <div className="max-w-xl">
              <span className="inline-block text-orange-600 font-black text-xs uppercase tracking-[0.25em] mb-4">
                What We Do
              </span>

              <h2 className="text-4xl md:text-5xl font-black uppercase text-slate-950 leading-none">
                Support A <span className="text-orange-500">Cause</span>
              </h2>

              <p className="mt-5 text-slate-500 text-sm md:text-base font-medium leading-relaxed">
                Choose a cause and support Sevaa Foundation&apos;s work in
                education, healthcare, livelihood, girl child support, women
                empowerment and humanitarian relief.
              </p>
            </div>

            <p className="text-slate-400 font-bold uppercase text-xs pb-2 border-b-2 border-orange-200">
              Explore Our Key Initiatives
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {causes.map((cause, index) => (
              <motion.article
                key={cause.title}
                whileHover={{ y: -10 }}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="relative h-[320px] rounded-3xl overflow-hidden group cursor-pointer shadow-md hover:shadow-xl transition-all duration-500"
              >
                <Link
                  to={cause.link}
                  className="block w-full h-full"
                  aria-label={`Learn more about ${cause.title} - ${cause.tag} initiative by Sevaa Foundation`}
                >
                  <img
                    src={cause.img}
                    alt={cause.alt}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>

                  <div className="absolute top-5 left-5">
                    <span className="bg-[#ffb000] text-black text-[10px] font-bold px-3 py-1 rounded-full uppercase shadow">
                      {cause.tag}
                    </span>
                  </div>

                  <div className="absolute bottom-6 left-5 right-5">
                    <p className="text-[#ffb000] text-[10px] font-black uppercase tracking-widest mb-2">
                      {cause.hindiTitle}
                    </p>

                    <h3 className="text-white text-xl font-black leading-tight mb-3 group-hover:text-[#ffb000] transition">
                      {cause.title}
                    </h3>

                    <span className="inline-flex text-white text-[11px] font-bold uppercase items-center gap-2 border-b border-white/40 pb-1 group-hover:border-[#ffb000] group-hover:text-[#ffb000] transition">
                      Learn More →
                    </span>

                    <p className="sr-only">{cause.keywords}</p>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatWeDo;